import { useEffect, useState } from "react";
import { Button, TextField, Typography, Container, Alert } from "@mui/material";
import { styled } from "@mui/system";
import { useCheckEmailQuery } from "../../api/useCheckEmail";
import { useSignUpQuery } from "../../api/useSignup";
import { useNavigate } from "react-router-dom";
import logo from "../../logo.jpg";

const ContainerStyled = styled(Container)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
});

const FormStyled = styled("form")({
  width: "100%", // Fix IE11 issue.
  marginTop: 1,
});

const SubmitButtonStyled = styled(Button)({
  marginTop: 3,
  marginBottom: 2,
});

function SignUpPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [checkEmailEnabled, setCheckEmailEnabled] = useState(false);
  const [signUpData, setSignUpData] = useState(null); // 회원가입 데이터 상태
  const { data, isLoading, isError, error } = useCheckEmailQuery(
    email,
    checkEmailEnabled
  );
  const {
    mutate: signUp,
    isPending,
    isError: isSignUpError,
    error: signUpError,
  } = useSignUpQuery();
  const isSignUpDisabled =
    !emailConfirmed ||
    password !== passwordConfirm ||
    !password ||
    !email ||
    isPending;

  const handleEmailChange = (event) => {
    setEmail(event.target.value.trim());
    setCheckEmailEnabled(false);
    setEmailConfirmed(false); // Reset email confirmed status on email change
    setErrorMessage("");
  };

  const handleConfirmEmail = () => {
    setCheckEmailEnabled(true);
  };

  const handleSignUp = (event) => {
    event.preventDefault();
    setSignUpData({ email, password });
  };

  useEffect(() => {
    if (data) {
      // 서버에서 이메일 확인 성공 응답을 받은 경우
      setEmailConfirmed(true);
      setName(data.user.name); // 응답에서 이름 정보를 가져옵니다.
      setErrorMessage("");
    }

    if (isError) {
      // 서버에서 이메일 확인 실패 응답을 받은 경우
      setEmailConfirmed(false);
      setErrorMessage(
        error.response.data.error ||
          "이메일 인증에 실패했습니다. 다시 시도해주세요"
      );
    }
    setCheckEmailEnabled(false);
  }, [data, isError, error]);

  useEffect(() => {
    if (emailConfirmed && signUpData) {
      // 이메일이 확인되고, signUpData가 설정되었을 때만 회원가입 요청 실행
      signUp(signUpData, {
        onSuccess: () => {
          // 회원가입 성공 시 로그인 페이지로 리다이렉트
          navigate("/login");
        },
        onError: (error) => {
          // 에러 처리 로직
          setErrorMessage(
            error.response.data.message || "회원가입에 실패했습니다."
          );
        },
      });
    }
  }, [emailConfirmed, signUpData, navigate, signUp]);

  useEffect(() => {
    if (isSignUpError && signUpError) {
      setErrorMessage(
        signUpError.response.data.message || "회원가입에 실패했습니다."
      );
    }
  }, [isSignUpError, signUpError]);
  return (
    <ContainerStyled component="main" maxWidth="xs">
      <img src={logo} alt="Logo" width={100} />
      <Typography component="h1" variant="h5">
        회원가입
      </Typography>
      <FormStyled noValidate onSubmit={handleSignUp}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="이메일 주소"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={handleEmailChange}
          error={!!errorMessage}
          helperText={errorMessage}
        />
        {isLoading ? (
          <div>loading</div>
        ) : (
          <Button
            variant="contained"
            onClick={handleConfirmEmail}
            sx={{ mt: 2, mb: 2 }}
          >
            이메일 확인
          </Button>
        )}

        {emailConfirmed && (
          <Typography sx={{ mb: 2 }}>이메일 확인 완료: {name}</Typography>
        )}
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="비밀번호"
          type="password"
          id="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value.trim())}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="passwordConfirm"
          label="비밀번호 확인"
          type="password"
          id="passwordConfirm"
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value.trim())}
        />
        <SubmitButtonStyled
          type="submit"
          fullWidth
          variant="contained"
          disabled={isSignUpDisabled}
        >
          {isPending ? "가입중" : "회원가입"}
        </SubmitButtonStyled>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      </FormStyled>
    </ContainerStyled>
  );
}

export default SignUpPage;
